/* SignInSheet — bottom-drawer "Continue with Google" sheet.
 *
 * Used by the paywall + by tapping the AccountChip when not signed in.
 */
import { motion, AnimatePresence } from "framer-motion";
import { GoogleLogo, X, Sparkle } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

export default function SignInSheet({ open, onClose, theme, headline, sub }) {
  const { signIn } = useAuth();
  const dark = theme?.id === "night" || theme?.id === "storm";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          data-testid="sign-in-sheet"
        >
          <motion.div
            className="absolute inset-0"
            style={{ background: "rgba(15,23,42,0.55)" }}
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            className="relative w-full sm:max-w-[420px] rounded-t-[28px] px-6 pb-8 pt-5"
            style={{
              background: dark ? "#0f172a" : "#fffbeb",
              border: `1px solid ${theme?.surfaceBorder || "rgba(15,23,42,0.08)"}`,
              boxShadow: "0 -20px 60px rgba(15,23,42,0.35)",
              paddingBottom: "calc(env(safe-area-inset-bottom) + 28px)",
            }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 24, stiffness: 240 }}
          >
            {/* Drag handle + close */}
            <div className="flex items-center justify-between mb-5">
              <div
                className="w-10 h-1 rounded-full mx-auto"
                style={{ background: dark ? "rgba(255,255,255,0.18)" : "rgba(15,23,42,0.18)" }}
              />
              <button
                type="button"
                onClick={onClose}
                aria-label="Close sign-in"
                data-testid="sign-in-close"
                className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center"
                style={{
                  background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
                  color: theme?.textSecondary || "#475569",
                }}
              >
                <X size={12} weight="bold" />
              </button>
            </div>

            <div
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full mb-3"
              style={{
                background: "rgba(245,158,11,0.14)",
                color: "#b45309",
                border: "1px solid rgba(245,158,11,0.3)",
              }}
            >
              <Sparkle size={11} weight="fill" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em]">
                7-day free Pro trial
              </span>
            </div>

            <h2
              className="font-display text-[22px] font-semibold leading-tight"
              style={{ color: theme?.textPrimary || "#0f172a", letterSpacing: "-0.01em" }}
            >
              {headline || "Sign in to unlock Nowcast Pro"}
            </h2>
            <p
              className="mt-1.5 text-[13px] leading-relaxed"
              style={{ color: theme?.textSecondary || "#475569" }}
            >
              {sub ||
                "One tap with Google. Hit-Rate verification, 7-day forecast, atmospheric Pulse, neighbourhood City Pulse — all yours for the next week, free."}
            </p>

            <button
              type="button"
              onClick={signIn}
              data-testid="continue-with-google"
              className="mt-6 w-full flex items-center justify-center gap-2.5 py-3.5 rounded-2xl font-semibold transition-transform active:scale-[0.985]"
              style={{
                background: "#ffffff",
                color: "#0f172a",
                border: "1px solid rgba(15,23,42,0.12)",
                boxShadow: "0 8px 22px rgba(15,23,42,0.18)",
              }}
            >
              <GoogleLogo size={18} weight="bold" />
              <span className="text-[14px]">Continue with Google</span>
            </button>

            <p
              className="mt-3 text-center text-[10px]"
              style={{ color: theme?.textMuted || "#94a3b8" }}
            >
              No card needed for the trial. ₹49/month after — cancel anytime.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
